/**
 * Prashasta Event Journey — Configuration Layer
 * All customer-facing copy, options, and data mappings live here.
 * Internal labels (END_TO_END, CUSTOM, etc.) are never shown to the customer.
 */

// ── Color tokens ────────────────────────────────────────────────────────────
export const PJ = {
  ivory:   '#FBF7F2',
  white:   '#FFFFFF',
  obsidian:'#201B19',
  ruby:    '#D91E48',
  gold:    '#C9A15F',
  blush:   '#F5E7E8',
  sand:    '#EEE4DA',
  forest:  '#35654D',
  slate:   '#746D69',
};

// ── Journey stage labels ─────────────────────────────────────────────────────
export const JOURNEY_STAGES = [
  { id: 'EVENT',   label: 'The Event'   },
  { id: 'NEEDS',   label: 'Your Needs'  },
  { id: 'STYLE',   label: 'Your Style'  },
  { id: 'DETAILS', label: 'The Details' },
  { id: 'PLAN',    label: 'Your Plan'   },
];

export const STEP_STAGES = {
  EVENT_TYPE:         'EVENT',
  WEDDING_FUNCTIONS:  'EVENT',
  CONFERENCE_TYPE:    'EVENT',
  PLANNING_SCOPE:     'NEEDS',
  SCOPE_CONFIRM:      'NEEDS',
  SERVICE_SELECTION:  'NEEDS',
  CITY:               'STYLE',
  STYLE_PREF:         'STYLE',
  GUEST_COUNT:        'DETAILS',
  VENUE_STATUS:       'DETAILS',
  DATE:               'DETAILS',
  ESTIMATE:           'PLAN',
  BUDGET:             'PLAN',   // retained for backward-compat with old saved drafts
  CONTACT:            'PLAN',
  RESULT:             'PLAN',
};

// ── Event types ──────────────────────────────────────────────────────────────
// image: config-driven — each tenant can swap with their own brand photography
// gradient: Prashasta brand fallback shown when image is unavailable
export const EVENT_TYPES = [
  {
    value: 'WEDDING',
    label: 'Wedding',
    description: 'From intimate rituals to grand celebrations',
    icon: 'Heart',
    image: 'https://images.unsplash.com/photo-1469371670807-013ccf25f16a?w=600&h=450&fit=crop&q=80',
    gradient: 'linear-gradient(145deg, #C9A15F 0%, #F5E7E8 60%, #FBF7F2 100%)',
  },
  {
    value: 'ENGAGEMENT',
    label: 'Engagement',
    description: 'Celebrate the beginning',
    icon: 'Gem',
    image: 'https://images.unsplash.com/photo-1522673607200-164d1b6ce486?w=600&h=450&fit=crop&q=80',
    gradient: 'linear-gradient(145deg, #F5E7E8 0%, #C9A15F 70%, #EEE4DA 100%)',
  },
  {
    value: 'BIRTHDAY',
    label: 'Birthday',
    description: 'Make the day unforgettable',
    icon: 'Gift',
    image: 'https://images.unsplash.com/photo-1527529482837-4698179dc6ce?w=600&h=450&fit=crop&q=80',
    gradient: 'linear-gradient(145deg, #D91E48 0%, #201B19 100%)',
  },
  {
    value: 'CORPORATE',
    label: 'Corporate Event',
    description: 'Experiences built around your brand',
    icon: 'Briefcase',
    image: 'https://images.unsplash.com/photo-1511578314322-379afb476865?w=600&h=450&fit=crop&q=80',
    gradient: 'linear-gradient(145deg, #201B19 0%, #746D69 100%)',
  },
  {
    value: 'CONFERENCE',
    label: 'Conference',
    description: 'From delegates to production',
    icon: 'MonitorPlay',
    image: 'https://images.unsplash.com/photo-1540575467063-178a50c2df87?w=600&h=450&fit=crop&q=80',
    gradient: 'linear-gradient(145deg, #201B19 0%, #35654D 100%)',
  },
  {
    value: 'PRODUCT_LAUNCH',
    label: 'Product Launch',
    description: 'Make the first impression count',
    icon: 'Zap',
    image: 'https://images.unsplash.com/photo-1505373877841-8d25f7d46678?w=600&h=450&fit=crop&q=80',
    gradient: 'linear-gradient(145deg, #201B19 0%, #D91E48 100%)',
  },
  {
    value: 'AWARDS',
    label: 'Award Ceremony',
    description: 'Celebrate achievement beautifully',
    icon: 'Award',
    image: 'https://images.unsplash.com/photo-1531058020387-3be344556be6?w=600&h=450&fit=crop&q=80',
    gradient: 'linear-gradient(145deg, #C9A15F 0%, #201B19 100%)',
  },
  {
    value: 'OTHER',
    label: 'Something Else',
    description: 'Tell us what you have in mind',
    icon: 'Sparkles',
    image: 'https://images.unsplash.com/photo-1513151233558-d860c5398176?w=600&h=450&fit=crop&q=80',
    gradient: 'linear-gradient(145deg, #746D69 0%, #35654D 100%)',
  },
];

// ── Wedding functions ────────────────────────────────────────────────────────
export const WEDDING_FUNCTIONS = [
  { value: 'ENGAGEMENT', label: 'Engagement' },
  { value: 'HALDI',      label: 'Haldi'      },
  { value: 'MEHENDI',    label: 'Mehendi'    },
  { value: 'SANGEETH',   label: 'Sangeet'    },
  { value: 'WEDDING',    label: 'Wedding'    },
  { value: 'RECEPTION',  label: 'Reception'  },
  { value: 'OTHER',      label: 'Other'      },
];

// ── Conference sub-types ─────────────────────────────────────────────────────
export const CONFERENCE_TYPES = [
  { value: 'CORPORATE_CONF', label: 'Corporate Conference', desc: 'Internal or external' },
  { value: 'PARTNER_SUMMIT', label: 'Partner Summit',       desc: 'Multi-stakeholder summits' },
  { value: 'LEADERSHIP_MEET',label: 'Leadership Meet',      desc: 'Executive gatherings' },
  { value: 'EXHIBITION',     label: 'Exhibition / Expo',    desc: 'Large-format exhibitions' },
  { value: 'CONF_AWARDS',    label: 'Conference + Awards',  desc: 'Combined event format' },
  { value: 'NOT_SURE',       label: 'Not Sure Yet',         desc: 'Help me figure it out' },
];

// ── Planning scopes ──────────────────────────────────────────────────────────
export const PLANNING_SCOPES = [
  {
    value: 'END_TO_END',
    label: 'Take care of everything',
    desc: 'From the first idea to event day.',
  },
  {
    value: 'CUSTOM',
    label: 'I need help with a few things',
    desc: "I'll choose the services I need.",
  },
];

// ── Services ─────────────────────────────────────────────────────────────────
export const SERVICES = [
  { value: 'VENUE',             label: 'Venue',             desc: 'Find the right place',      icon: 'MapPin'          },
  { value: 'DECOR',             label: 'Decor & Styling',   desc: 'Create the look',           icon: 'Sparkles'        },
  { value: 'CATERING',          label: 'Food & Catering',   desc: 'Plan the menu',             icon: 'UtensilsCrossed' },
  { value: 'PHOTOGRAPHY',       label: 'Photography',       desc: 'Capture the memories',      icon: 'Camera'          },
  { value: 'AV_PRODUCTION',     label: 'AV & Production',   desc: 'Stage, sound and screens',  icon: 'Mic2'            },
  { value: 'ENTERTAINMENT',     label: 'Entertainment',     desc: 'Set the mood',              icon: 'Music'           },
  { value: 'GUEST_HOSPITALITY', label: 'Guest Hospitality', desc: 'Take care of your guests',  icon: 'Users'           },
  { value: 'TRANSPORT',         label: 'Transport',         desc: 'Move everyone smoothly',    icon: 'Car'             },
  { value: 'INVITATIONS',       label: 'Invitations & RSVP',desc: 'Keep everyone connected',   icon: 'Mail'            },
];

export const END_TO_END_DEFAULTS = ['VENUE', 'DECOR', 'CATERING', 'AV_PRODUCTION', 'GUEST_HOSPITALITY', 'PHOTOGRAPHY'];

// ── Cities ───────────────────────────────────────────────────────────────────
export const CITIES = [
  { value: 'HYDERABAD', label: 'Hyderabad' },
  { value: 'BENGALURU', label: 'Bengaluru' },
  { value: 'CHENNAI',   label: 'Chennai'   },
  { value: 'MUMBAI',    label: 'Mumbai'    },
  { value: 'DELHI',     label: 'Delhi NCR' },
  { value: 'OTHER',     label: 'Other'     },
];

// ── Guest count ranges ───────────────────────────────────────────────────────
export const GUEST_RANGES = [
  { value: 'UNDER_100',   label: 'Under 100',    display: 'Under 100'   },
  { value: '100_250',     label: '100 – 250',    display: '100 – 250'   },
  { value: '250_500',     label: '250 – 500',    display: '250 – 500'   },
  { value: '500_750',     label: '500 – 750',    display: '500 – 750'   },
  { value: '750_1000',    label: '750 – 1,000',  display: '750 – 1,000' },
  { value: '1000_PLUS',   label: '1,000+',       display: '1,000+'      },
  { value: 'NOT_SURE',    label: 'Not Sure Yet', display: 'TBD'         },
];

// ── Venue statuses ───────────────────────────────────────────────────────────
export const VENUE_STATUSES = [
  { value: 'CONFIRMED',     label: "Yes, it's confirmed", desc: 'Venue already booked'      },
  { value: 'SHORTLISTED',   label: "I'm shortlisting",    desc: 'Looking at a few options'  },
  { value: 'SEARCHING',     label: 'Not yet',             desc: "Haven't started looking"   },
  { value: 'HELP_REQUIRED', label: 'Help me find one',    desc: "I'd love recommendations"  },
];

// ── Style options ─────────────────────────────────────────────────────────────
export const STYLE_OPTIONS = [
  { value: 'ELEGANT',      label: 'Elegant'      },
  { value: 'TRADITIONAL',  label: 'Traditional'  },
  { value: 'CONTEMPORARY', label: 'Contemporary' },
  { value: 'MINIMAL',      label: 'Minimal'      },
  { value: 'FLORAL',       label: 'Floral'       },
  { value: 'LUXURY',       label: 'Luxury'       },
  { value: 'VIBRANT',      label: 'Vibrant'      },
  { value: 'NOT_SURE',     label: 'Not Sure Yet' },
];

// ── Budget ranges (configurable — change here without touching components) ────
export const BUDGET_RANGES = [
  { value: 'UNDER_10L', label: 'Under ₹10L',    display: 'Under ₹10L'  },
  { value: '10L_20L',   label: '₹10L – ₹20L',   display: '₹10L – ₹20L' },
  { value: '20L_35L',   label: '₹20L – ₹35L',   display: '₹20L – ₹35L' },
  { value: '35L_50L',   label: '₹35L – ₹50L',   display: '₹35L – ₹50L' },
  { value: '50L_PLUS',  label: '₹50L+',          display: '₹50L+'       },
  { value: 'DISCUSS',   label: "I'd rather discuss this", display: 'To discuss' },
];

// ── Empty journey state factory ───────────────────────────────────────────────
export function createEmptyJourney(overrides = {}) {
  return {
    session_id:      crypto.randomUUID ? crypto.randomUUID() : `pj-${Date.now()}`,
    started_at:      new Date().toISOString(),
    last_updated_at: new Date().toISOString(),
    source_channel:  'WEBSITE_JOURNEY',
    journey_step:    'EVENT_TYPE',
    journey_completion: 0,
    ...overrides,
  };
}

// ── Service display helper ────────────────────────────────────────────────────
export function getServiceLabel(value) {
  return SERVICES.find(s => s.value === value)?.label || value;
}

export function getEventTypeLabel(value) {
  return EVENT_TYPES.find(e => e.value === value)?.label || value;
}

export function getCityDisplay(state) {
  if (state.city?.value === 'OTHER') return state.city_custom || 'Other';
  return CITIES.find(c => c.value === state.city?.value)?.label || state.city?.value || '';
}

// ── Date availability simulation (deterministic, frontend-only) ───────────────
export function checkDateAvailability(dateStr) {
  if (!dateStr) return null;
  const selected = new Date(dateStr);
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  if (selected < today) return { status: 'PAST' };

  const daysAhead = Math.floor((selected - today) / (1000 * 60 * 60 * 24));

  if (daysAhead < 7) return { status: 'LIMITED', note: 'Very short planning window' };
  if (daysAhead < 21) return { status: 'LIMITED', note: 'Tight timeline, but possible' };
  return { status: 'AVAILABLE' };
}

export function formatEventDate(dateStr) {
  if (!dateStr) return '';
  try {
    return new Date(dateStr).toLocaleDateString('en-IN', {
      weekday: 'long', day: 'numeric', month: 'long', year: 'numeric'
    });
  } catch { return dateStr; }
}

export function formatEventDateShort(dateStr) {
  if (!dateStr) return '';
  try {
    return new Date(dateStr).toLocaleDateString('en-IN', {
      day: 'numeric', month: 'long', year: 'numeric'
    });
  } catch { return dateStr; }
}
